import { supabase } from "@/lib/supabaseClient";
import { logger } from "@/utils/logger";
import pointsConfig, { AI_POINTS } from "@/config/pointsConfig";

/**
 * Interface lịch sử điểm thưởng
 */
export interface PointsHistory {
  id: string;
  user_id: string;
  points: number;
  action_type: string;
  content_type: string;
  content_id: string | null;
  description: string;
  created_at: string;
  transaction_id?: string;
  payment_method?: string;
  ip_address?: string;
}

/**
 * Service quản lý điểm thưởng
 */
export class RewardService {
  /**
   * Lấy số điểm hiện tại của người dùng
   * @returns Số điểm hiện tại
   */
  async getCurrentPoints(): Promise<number> {
    try {
      const { data: userData, error } = await supabase.auth.getUser();

      if (error) {
        throw new Error("Không thể xác thực người dùng");
      }

      const userId = userData.user?.id;

      if (!userId) {
        throw new Error("Không tìm thấy thông tin người dùng");
      }

      const { data, error: fetchError } = await supabase
        .from("users")
        .select("ai_credits")
        .eq("id", userId)
        .single();

      if (fetchError) {
        throw new Error("Không thể lấy thông tin điểm thưởng");
      }

      return data?.ai_credits || 0;
    } catch (error) {
      logger.error("Error getting current points:", error);
      throw error;
    }
  }

  /**
   * Lấy lịch sử điểm thưởng của người dùng
   * @param limit Số lượng bản ghi tối đa
   * @param offset Vị trí bắt đầu
   * @returns Lịch sử điểm thưởng
   */
  async getPointsHistory(limit = 20, offset = 0) {
    try {
      const { data: userData, error } = await supabase.auth.getUser();

      if (error) {
        throw new Error("Không thể xác thực người dùng");
      }

      const userId = userData.user?.id;

      if (!userId) {
        throw new Error("Không tìm thấy thông tin người dùng");
      }

      const {
        data,
        error: fetchError,
        count,
      } = await supabase
        .from("reward_points_history")
        .select("*", { count: "exact" })
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (fetchError) {
        throw new Error("Không thể lấy lịch sử điểm thưởng");
      }

      return {
        data: data || [],
        total: count || 0,
        limit,
        offset,
      };
    } catch (error) {
      logger.error("Error getting points history:", error);
      throw error;
    }
  }

  /**
   * Trừ điểm khi sử dụng dịch vụ AI
   * @param points Số điểm cần trừ
   * @param serviceType Loại dịch vụ
   * @param contentId ID của nội dung
   * @param description Mô tả
   * @returns Kết quả trừ điểm
   */
  async deductPointsForAI(
    points: number,
    serviceType: string,
    contentId?: string,
    description?: string,
  ) {
    try {
      const { data: userData, error } = await supabase.auth.getUser();

      if (error) {
        throw new Error("Không thể xác thực người dùng");
      }

      const userId = userData.user?.id;

      if (!userId) {
        throw new Error("Không tìm thấy thông tin người dùng");
      }

      // Kiểm tra người dùng có đủ điểm không
      const currentPoints = await this.getCurrentPoints();

      if (currentPoints < points) {
        return {
          success: false,
          message: "Không đủ điểm thưởng để sử dụng dịch vụ này",
        };
      }

      // Trừ điểm
      const { data, error: deductError } = await supabase
        .from("reward_points_history")
        .insert({
          user_id: userId,
          points: -points,
          action_type: "ai_usage",
          content_type: "ai_service",
          content_id: contentId || serviceType,
          description: description || `Sử dụng dịch vụ ${serviceType}`,
        })
        .select()
        .single();

      if (deductError) {
        throw new Error("Không thể trừ điểm thưởng");
      }

      // Ghi nhận việc sử dụng AI
      await supabase.from("user_ai_usage").insert({
        user_id: userId,
        service_type: serviceType,
        points_cost: points,
        description: description || `Sử dụng dịch vụ ${serviceType}`,
      });

      return {
        success: true,
        message: `Đã trừ ${points} điểm cho việc sử dụng dịch vụ AI`,
        data,
      };
    } catch (error) {
      logger.error("Error deducting points for AI:", error);
      throw error;
    }
  }

  /**
   * Thêm điểm thưởng khi hoàn thành bài học
   * @param lessonId ID bài học
   * @param lessonTitle Tiêu đề bài học
   * @returns Kết quả thêm điểm
   */
  async addLessonCompletionPoints(lessonId: string, lessonTitle?: string) {
    try {
      const { data: userData, error } = await supabase.auth.getUser();

      if (error) {
        throw new Error("Không thể xác thực người dùng");
      }

      const userId = userData.user?.id;

      if (!userId) {
        throw new Error("Không tìm thấy thông tin người dùng");
      }

      // Mặc định thêm 5 điểm cho mỗi bài học hoàn thành
      const points = 5;
      const description = `Hoàn thành bài học: ${lessonTitle || lessonId}`;

      const { data, error: addError } = await supabase
        .from("reward_points_history")
        .insert({
          user_id: userId,
          points,
          action_type: "learning",
          content_type: "lesson",
          content_id: lessonId,
          description,
        })
        .select()
        .single();

      if (addError) {
        throw new Error("Không thể thêm điểm thưởng");
      }

      return {
        success: true,
        message: `Đã thêm ${points} điểm cho việc hoàn thành bài học`,
        data,
      };
    } catch (error) {
      logger.error("Error adding lesson completion points:", error);
      throw error;
    }
  }

  /**
   * Thêm điểm thưởng khi hoàn thành khóa học
   * @param courseId ID khóa học
   * @param courseTitle Tiêu đề khóa học
   * @returns Kết quả thêm điểm
   */
  async addCourseCompletionPoints(courseId: string, courseTitle?: string) {
    try {
      const { data: userData, error } = await supabase.auth.getUser();

      if (error) {
        throw new Error("Không thể xác thực người dùng");
      }

      const userId = userData.user?.id;

      if (!userId) {
        throw new Error("Không tìm thấy thông tin người dùng");
      }

      // Mặc định thêm 20 điểm cho mỗi khóa học hoàn thành
      const points = 20;
      const description = `Hoàn thành khóa học: ${courseTitle || courseId}`;

      const { data, error: addError } = await supabase
        .from("reward_points_history")
        .insert({
          user_id: userId,
          points,
          action_type: "learning",
          content_type: "course",
          content_id: courseId,
          description,
        })
        .select()
        .single();

      if (addError) {
        throw new Error("Không thể thêm điểm thưởng");
      }

      return {
        success: true,
        message: `Đã thêm ${points} điểm cho việc hoàn thành khóa học`,
        data,
      };
    } catch (error) {
      logger.error("Error adding course completion points:", error);
      throw error;
    }
  }

  /**
   * Lấy số điểm cần thiết cho một dịch vụ AI cụ thể
   * @param serviceType Loại dịch vụ (chat, generate, etc.)
   * @param contentType Loại nội dung (lesson, course, etc.)
   * @returns Số điểm cần thiết
   */
  getRequiredPointsForService(serviceType: string, contentType?: string): number {
    const service = serviceType.toUpperCase();
    
    if (service === 'CHAT' && contentType) {
      const type = contentType.toUpperCase();
      return AI_POINTS.CHAT[type] || AI_POINTS.CHAT.BASIC;
    }
    
    if (service === 'GENERATE') {
      return AI_POINTS.GENERATE.SUMMARY; // Mặc định là summary
    }
    
    // Mặc định trả về điểm cơ bản cho chat
    return AI_POINTS.CHAT.BASIC;
  }

  /**
   * Thêm điểm thưởng cho người dùng
   * @param userId ID người dùng
   * @param points Số điểm thưởng
   * @param actionType Loại hành động
   * @param contentType Loại nội dung
   * @param contentId ID nội dung
   * @param description Mô tả
   * @returns Kết quả thêm điểm
   */
  async addPoints(
    userId: string,
    points: number,
    actionType: string,
    contentType: string,
    contentId: string | null,
    description: string
  ) {
    try {
      // Thêm vào lịch sử điểm thưởng
      const { data, error } = await supabase
        .from('reward_points_history')
        .insert({
          user_id: userId,
          points,
          action_type: actionType,
          content_type: contentType,
          content_id: contentId,
          description,
        })
        .select()
        .single();
      
      if (error) {
        throw new Error(`Không thể thêm điểm thưởng: ${error.message}`);
      }
      
      // Cập nhật tổng điểm của người dùng
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('ai_credits')
        .eq('id', userId)
        .single();
      
      if (userError) {
        throw new Error(`Không thể lấy thông tin người dùng: ${userError.message}`);
      }
      
      const currentCredits = userData.ai_credits || 0;
      const newCredits = currentCredits + points;
      
      const { error: updateError } = await supabase
        .from('users')
        .update({ ai_credits: newCredits })
        .eq('id', userId);
      
      if (updateError) {
        throw new Error(`Không thể cập nhật điểm thưởng: ${updateError.message}`);
      }
      
      return {
        success: true,
        data,
        newCredits,
      };
    } catch (error) {
      logger.error('Error adding points:', error);
      throw error;
    }
  }
  
  /**
   * Sử dụng điểm thưởng của người dùng
   * @param userId ID người dùng
   * @param points Số điểm thưởng (giá trị âm)
   * @param actionType Loại hành động
   * @param contentType Loại nội dung
   * @param contentId ID nội dung
   * @param description Mô tả
   * @returns Kết quả sử dụng điểm
   */
  async usePoints(
    userId: string,
    points: number,
    actionType: string,
    contentType: string,
    contentId: string | null,
    description: string
  ) {
    try {
      // Đảm bảo points là số âm
      const pointsToUse = Math.abs(points) * -1;
      
      // Kiểm tra người dùng có đủ điểm không
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('ai_credits')
        .eq('id', userId)
        .single();
      
      if (userError) {
        throw new Error(`Không thể lấy thông tin người dùng: ${userError.message}`);
      }
      
      const currentCredits = userData.ai_credits || 0;
      
      if (currentCredits + pointsToUse < 0) {
        throw new Error('Không đủ điểm để thực hiện hành động này');
      }
      
      // Thêm vào lịch sử điểm thưởng
      const { data, error } = await supabase
        .from('reward_points_history')
        .insert({
          user_id: userId,
          points: pointsToUse,
          action_type: actionType,
          content_type: contentType,
          content_id: contentId,
          description,
        })
        .select()
        .single();
      
      if (error) {
        throw new Error(`Không thể cập nhật lịch sử điểm: ${error.message}`);
      }
      
      // Cập nhật tổng điểm của người dùng
      const newCredits = currentCredits + pointsToUse;
      
      const { error: updateError } = await supabase
        .from('users')
        .update({ ai_credits: newCredits })
        .eq('id', userId);
      
      if (updateError) {
        throw new Error(`Không thể cập nhật điểm thưởng: ${updateError.message}`);
      }
      
      return {
        success: true,
        data,
        newCredits,
      };
    } catch (error) {
      logger.error('Error using points:', error);
      throw error;
    }
  }
  
  /**
   * Kiểm tra người dùng có đủ điểm để thực hiện hành động không
   * @param userId ID người dùng
   * @param requiredPoints Số điểm yêu cầu
   * @returns Kết quả kiểm tra
   */
  async hasEnoughPoints(userId: string, requiredPoints: number) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('ai_credits')
        .eq('id', userId)
        .single();
      
      if (error) {
        throw new Error(`Không thể lấy thông tin người dùng: ${error.message}`);
      }
      
      const currentCredits = data.ai_credits || 0;
      
      return {
        hasEnough: currentCredits >= requiredPoints,
        currentCredits,
        requiredPoints,
      };
    } catch (error) {
      logger.error('Error checking user points:', error);
      throw error;
    }
  }
}

export const rewardService = new RewardService();
export default rewardService;
